import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useBrandManager } from "@/hooks/useBrandManager";

export interface InternalTrainingShare {
  id: string;
  brand_id: string;
  user_id: string;
  name: string;
  share_type: string;
  share_code: string;
  access_pin: string | null;
  report_config: Record<string, any>;
  is_active: boolean;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
  schedule?: InternalTrainingShareSchedule | null;
}

export interface InternalTrainingShareSchedule {
  id: string;
  share_id: string;
  brand_id: string;
  schedule_type: string;
  schedule_time: string;
  schedule_days: number[];
  schedule_day_of_month: number | null;
  timezone: string;
  email_subject: string | null;
  email_template: string | null;
  is_enabled: boolean;
  last_sent_at: string | null;
  next_send_at: string | null;
  created_at: string;
}

export interface CreateShareInput {
  name: string;
  share_type: string;
  access_pin?: string;
  report_config?: Record<string, any>;
  expires_at?: string;
}

export interface CreateScheduleInput {
  share_id: string;
  schedule_type: string;
  schedule_time?: string;
  schedule_days?: number[];
  schedule_day_of_month?: number;
  timezone?: string;
  email_subject?: string;
  email_template?: string;
  is_enabled?: boolean;
}

const generateShareCode = () => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

const calculateNextRunAt = (scheduleType: string, scheduleTime: string, scheduleDays?: number[], scheduleDayOfMonth?: number): string => {
  const now = new Date();
  const [hours, minutes] = (scheduleTime || '09:00').split(':').map(Number);
  
  const next = new Date(now);
  next.setHours(hours, minutes, 0, 0);
  
  if (next <= now) {
    next.setDate(next.getDate() + 1);
  }

  switch (scheduleType) {
    case 'daily':
      break;
    case 'weekly':
      const days = scheduleDays || [1];
      let found = false;
      for (let i = 0; i < 7; i++) {
        const checkDate = new Date(next);
        checkDate.setDate(next.getDate() + i);
        if (days.includes(checkDate.getDay())) {
          next.setDate(checkDate.getDate());
          found = true;
          break;
        }
      }
      if (!found) {
        next.setDate(next.getDate() + 7);
      }
      break;
    case 'monthly':
      const dayOfMonth = scheduleDayOfMonth || 1;
      next.setDate(dayOfMonth);
      if (next <= now) {
        next.setMonth(next.getMonth() + 1);
      }
      break;
  }

  return next.toISOString();
};

export const useInternalTrainingShares = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentBrandId } = useBrandManager();

  const { data: shares, isLoading } = useQuery({
    queryKey: ['internal-training-shares', currentBrandId],
    queryFn: async () => {
      if (!currentBrandId) return [];

      const { data: sharesData, error: sharesError } = await (supabase as any)
        .from('os_internal_training_share_records')
        .select('*')
        .eq('brand_id', currentBrandId)
        .order('created_at', { ascending: false });

      if (sharesError) throw sharesError;

      // Fetch schedules from job_schedules
      const { data: schedules, error: schedulesError } = await supabase
        .from('job_schedules')
        .select('*')
        .eq('brand_id', currentBrandId)
        .eq('job_type', 'internal_training_share');

      if (schedulesError) throw schedulesError;

      // Map schedules to shares by share_id in parameters
      const scheduleMap = new Map<string, any>();
      schedules?.forEach((s: any) => {
        const shareId = s.parameters?.share_id;
        if (shareId) {
          scheduleMap.set(shareId, {
            id: s.id,
            share_id: shareId,
            brand_id: s.brand_id,
            schedule_type: s.parameters?.schedule_type || 'daily',
            schedule_time: s.parameters?.schedule_time || '09:00',
            schedule_days: s.parameters?.schedule_days || [],
            schedule_day_of_month: s.parameters?.schedule_day_of_month || null,
            timezone: s.parameters?.timezone || 'UTC',
            email_subject: s.parameters?.email_subject || null,
            email_template: s.parameters?.email_template || null,
            is_enabled: s.enabled ?? true,
            last_sent_at: s.last_run_at,
            next_send_at: s.next_run_at,
            created_at: s.created_at,
          });
        }
      });

      return (sharesData || []).map((share: any) => ({
        ...share,
        schedule: scheduleMap.get(share.id) || null
      })) as InternalTrainingShare[];
    },
    enabled: !!currentBrandId,
  });

  const createShare = useMutation({
    mutationFn: async (input: CreateShareInput): Promise<InternalTrainingShare> => {
      if (!currentBrandId) throw new Error('No brand selected');

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('Not authenticated');

      const shareCode = generateShareCode();

      const { data, error } = await (supabase as any)
        .from('os_internal_training_share_records')
        .insert({
          brand_id: currentBrandId,
          user_id: userData.user.id,
          share_code: shareCode,
          ...input,
        })
        .select()
        .single();

      if (error) throw error;
      return data as InternalTrainingShare;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['internal-training-shares', currentBrandId] });
      toast({
        title: "Success",
        description: "Progress share created successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to create progress share",
        variant: "destructive",
      });
      console.error('Error creating progress share:', error);
    },
  });

  const updateShare = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<InternalTrainingShare> & { id: string }) => {
      const { data, error } = await (supabase as any)
        .from('os_internal_training_share_records')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['internal-training-shares', currentBrandId] });
      toast({
        title: "Success",
        description: "Progress share updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update progress share",
        variant: "destructive",
      });
      console.error('Error updating progress share:', error);
    },
  });

  const deleteShare = useMutation({
    mutationFn: async (id: string) => {
      // Also delete associated schedule
      const { data: schedules } = await supabase
        .from('job_schedules')
        .select('id')
        .eq('job_type', 'internal_training_share');

      const matchingSchedules = (schedules || []).filter((s: any) => {
        const params = s.parameters as any;
        return params?.share_id === id;
      });

      if (matchingSchedules.length) {
        await supabase
          .from('job_schedules')
          .delete()
          .in('id', matchingSchedules.map(s => s.id));
      }

      const { error } = await (supabase as any)
        .from('os_internal_training_share_records')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['internal-training-shares', currentBrandId] });
      queryClient.invalidateQueries({ queryKey: ['job-schedules'] });
      toast({
        title: "Success",
        description: "Progress share deleted successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to delete progress share",
        variant: "destructive",
      });
      console.error('Error deleting progress share:', error);
    },
  });

  const createSchedule = useMutation({
    mutationFn: async (input: CreateScheduleInput) => {
      if (!currentBrandId) throw new Error('No brand selected');

      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('Not authenticated');

      const { data: share } = await (supabase as any)
        .from('os_internal_training_share_records')
        .select('name')
        .eq('id', input.share_id)
        .single();

      const nextRunAt = calculateNextRunAt(
        input.schedule_type || 'daily',
        input.schedule_time || '09:00',
        input.schedule_days,
        input.schedule_day_of_month
      );

      const { data, error } = await supabase
        .from('job_schedules')
        .insert({
          brand_id: currentBrandId,
          user_id: userData.user.id,
          job_type: 'internal_training_share',
          name: `Progress Share: ${share?.name || 'Unknown'}`,
          description: `Scheduled progress share delivery`,
          enabled: input.is_enabled ?? true,
          next_run_at: nextRunAt,
          parameters: {
            share_id: input.share_id,
            schedule_type: input.schedule_type || 'daily',
            schedule_time: input.schedule_time || '09:00',
            schedule_days: input.schedule_days || [],
            schedule_day_of_month: input.schedule_day_of_month || null,
            timezone: input.timezone || 'UTC',
            email_subject: input.email_subject || null,
            email_template: input.email_template || null,
          },
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['internal-training-shares', currentBrandId] });
      queryClient.invalidateQueries({ queryKey: ['job-schedules'] });
      toast({
        title: "Success",
        description: "Schedule created successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to create schedule",
        variant: "destructive",
      });
      console.error('Error creating schedule:', error);
    },
  });

  const updateSchedule = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<InternalTrainingShareSchedule> & { id: string }) => {
      const { data: currentSchedule } = await supabase
        .from('job_schedules')
        .select('parameters')
        .eq('id', id)
        .single();

      const currentParams = (currentSchedule?.parameters as Record<string, any>) || {};

      const nextRunAt = calculateNextRunAt(
        updates.schedule_type || currentParams.schedule_type || 'daily',
        updates.schedule_time || currentParams.schedule_time || '09:00',
        updates.schedule_days || currentParams.schedule_days,
        updates.schedule_day_of_month || currentParams.schedule_day_of_month
      );

      const { data, error } = await supabase
        .from('job_schedules')
        .update({
          enabled: updates.is_enabled,
          next_run_at: nextRunAt,
          parameters: {
            ...currentParams,
            schedule_type: updates.schedule_type ?? currentParams.schedule_type,
            schedule_time: updates.schedule_time ?? currentParams.schedule_time,
            schedule_days: updates.schedule_days ?? currentParams.schedule_days,
            schedule_day_of_month: updates.schedule_day_of_month ?? currentParams.schedule_day_of_month,
            timezone: updates.timezone ?? currentParams.timezone,
            email_subject: updates.email_subject ?? currentParams.email_subject,
            email_template: updates.email_template ?? currentParams.email_template,
          },
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['internal-training-shares', currentBrandId] });
      queryClient.invalidateQueries({ queryKey: ['job-schedules'] });
      toast({
        title: "Success",
        description: "Schedule updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update schedule",
        variant: "destructive",
      });
      console.error('Error updating schedule:', error);
    },
  });

  return {
    shares,
    isLoading,
    createShare,
    updateShare,
    deleteShare,
    createSchedule,
    updateSchedule,
  };
};
